import type { SeatPriceGroupData } from '@/types';

/**
 * Static palette of 100 visually distinct colours used to differentiate
 * price groups on the seatmap canvas and in the price legend modal.
 */
export const PRICE_GROUP_PALETTE: string[] = [
  '7EA5E5', '05BDFF', '5705BD', 'D974F5', '9A52FD', 'F5B562',
  '63D0BF', '3C6BAA', '9EA669', '785A4B', 'C7BCF8', '3F7F3F',
  '7BA2CC', '5705BD', '5CC6D8', 'F7932F', '3F57E0', 'A8D05F',
  '38D97A', 'C9D23F', 'C2A80A', '7B73B0', '5FD6C2', '8FA0E0',
  'dfe619', '3CB44B', '4363D8', 'F58231', '911EB4',
  '42D4F4', 'F032E6', 'BFEF45', 'FABED4', '469990',
  'DCBEFF', '9A6324', 'FFFAC8', '800000', 'AAFFC3',
  '808000', 'FFD8B1', '000075', 'A9A9A9', 'E6BEFF',
  'FF6F61', '6B5B95', '88B04B', 'F7CAC9', '92A8D1',
  '955251', 'B565A7', '009B77', 'DD4124', 'D65076',
  '45B8AC', 'EFC050', '5B5EA6', '9B2335', 'DFCFBE',
  '55B4B0', 'E15D44', '7FCDCD', 'BC243C', 'C3447A',
  '98B4D4', 'BFD641', '2A4B7C', 'F0EAD6', '577284',
  'F7786B', '91A8D0', 'FCEDDA', 'ED2B33', 'D7C49E',
  '343148', 'A2B9BC', 'B8860B', '6C3461', '5CDB95',
  'EDF5E1', '379683', '8EE4AF', '05386B', 'FC4445',
  '3FEEE6', '55BCC9', '97CAEF', 'CAFAFE', '86A8E7',
  'D16BA5', 'FF9A76', '679186', 'BCD4DE', 'FBC687',
  'EA907A', 'AACDBE', '9AD0EC', 'D4A5A5', '392F5A',
  'F092B0', 'B0E0E6', 'C4A35A', '7C73E6', 'E8D21D',
  'FF00FF', '39FF14', 'FF6700', '0096FF', 'FFD700',
  'CC5599', '228B22', 'FF4500', '1E90FF', 'DAA520',
  '8B008B', '00CED1', 'FF1493', '7B68EE', '00FA9A',
  'FF8C00', '9370DB', '20B2AA', 'CD853F', 'DC143C',
];

type PriceGroups = Record<string, Record<string, SeatPriceGroupData>>;

export interface PriceGroupColorEntry {
  id: number;
  name: string;
  price: number;
  color: string;
  seatCount: number;
  eventId: number | string;
  ticketId: number;
}

/**
 * Build a list of price groups with assigned colours, one entry per
 * (ticketId × groupId) combination.
 *
 * Two-pass approach:
 *  Pass 1 — collect unique group IDs in encounter order and assign a
 *            palette colour to each.
 *  Pass 2 — emit one PriceGroupColorEntry per (ticketId, groupId) pair,
 *            reusing the colour assigned in pass 1.
 *
 * This ensures that:
 *  - The same group ID always gets the same colour (canvas consistency).
 *  - When multiple tickets share the same seatmap, each ticket's price
 *    groups appear as separate entries so the legend shows all prices.
 */
export function buildPriceGroupColorList(priceGroups: PriceGroups | undefined): PriceGroupColorEntry[] {
  if (!priceGroups) return [];

  // Pass 1: assign a stable colour to each unique group ID.
  const groupColors = new Map<number, string>();
  for (const ticketGroups of Object.values(priceGroups)) {
    for (const group of Object.values(ticketGroups)) {
      if (groupColors.has(group.id)) continue;
      groupColors.set(group.id, PRICE_GROUP_PALETTE[groupColors.size % PRICE_GROUP_PALETTE.length]);
    }
  }

  // Pass 2: emit one entry per (ticketId, groupId) pair.
  const result: PriceGroupColorEntry[] = [];
  const emitted = new Set<string>();

  for (const [ticketIdStr, ticketGroups] of Object.entries(priceGroups)) {
    for (const group of Object.values(ticketGroups)) {
      const key = `${ticketIdStr}-${group.id}`;
      if (emitted.has(key)) continue;
      emitted.add(key);

      let seatCount = 0;
      for (let i = 0; i < group.seats.length; i++) {
        if (group.seats[i]) seatCount++;
      }

      result.push({
        id: group.id,
        name: group.name,
        price: group.price,
        color: groupColors.get(group.id)!,
        seatCount,
        eventId: group.event_id,
        ticketId: group.ticket_id,
      });
    }
  }

  return result;
}

/**
 * Build a Map from price-group ID to hex colour string (without `#`).
 */
export function buildPriceGroupColorMap(priceGroups: PriceGroups | undefined): Map<number, string> {
  const list = buildPriceGroupColorList(priceGroups);
  const map = new Map<number, string>();
  for (const entry of list) {
    map.set(entry.id, entry.color);
  }
  return map;
}

/**
 * Pre-compute a colour for every seat in a single pass.
 *
 * Returns a sparse array indexed by seat index. `result[i]` is the hex
 * colour string (without `#`) for seat `i`, or `undefined` if the seat
 * does not belong to any price group.
 *
 * When a seat belongs to multiple price groups (across different tickets),
 * the group with the most total seats is chosen for visual uniformity.
 *
 * Complexity: O(G × S) where G = number of groups and S = max seat-array
 * length — each group's seat array is iterated exactly once.
 */
export function buildSeatColorArray(
  priceGroups: PriceGroups | undefined,
): (string | undefined)[] {
  if (!priceGroups) return [];

  const colorMap = buildPriceGroupColorMap(priceGroups);

  // 1. Count seats per group (deduplicated by id)
  const groupSeatCounts = new Map<number, number>();
  for (const ticketGroups of Object.values(priceGroups)) {
    for (const group of Object.values(ticketGroups)) {
      if (groupSeatCounts.has(group.id)) continue;
      let count = 0;
      const seats = group.seats;
      for (let i = 0; i < seats.length; i++) {
        if (seats[i]) count++;
      }
      groupSeatCounts.set(group.id, count);
    }
  }

  // 2. Assign each seat to the group with the highest seat count
  const result: (string | undefined)[] = [];
  const bestCount: number[] = []; // parallel array tracking the winning count

  for (const ticketGroups of Object.values(priceGroups)) {
    for (const group of Object.values(ticketGroups)) {
      const color = colorMap.get(group.id);
      if (!color) continue;
      const count = groupSeatCounts.get(group.id) ?? 0;
      const seats = group.seats;
      for (let i = 0; i < seats.length; i++) {
        if (!seats[i]) continue;
        if (result[i] === undefined || count > (bestCount[i] ?? -1)) {
          result[i] = color;
          bestCount[i] = count;
        }
      }
    }
  }

  return result;
}
